import React, { useEffect, useContext, useState, useMemo, useCallback } from 'react';
import { useFormik } from "formik";
import * as Yup from "yup";
import { IoClose } from 'react-icons/io5';
import { BsPlusCircle } from "react-icons/bs";
import { FaTrash } from 'react-icons/fa';
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';

import { updateCategorias } from "../../services/api";
import { AuthContext } from "../../contexts/auth";
import '../../styles/modal-shared.css';

// Chaves em pt sao os VALORES gravados no banco (dia da semana). Apenas o rotulo
// exibido no chip vem do i18n (schedule.days.*).
const DAY_KEYS = ["domingo", "segunda", "terca", "quarta", "quinta", "sexta", "sabado"];

const ModalEditCategoria = ({ setEditCategoria, showEditCategoria, setRefresh,
    _idCategoriaToEdit,
    id_categoria,
    _modeloToEdit,
    _titleToEdit,
    _disponibilidadeToEdit,
    setDisponibilidadeToEdit,
    _diaHorarioDispToEdit,
    setDiaHorarioDispToEdit
}) => {
    const { t } = useTranslation('categorias');
    const { user } = useContext(AuthContext);
    const empresa = localStorage.getItem('empresa');
    const empresaParse = JSON.parse(empresa);
    const idEmpresa = empresaParse.id_empresa;

    const options = useMemo(() => [
        { value: "sempre", label: t('availability.always') },
        { value: "pausado", label: t('availability.paused') },
        { value: "especificos", label: t('availability.scheduled') }
    ], [t]);

    const optionsModelo = useMemo(() => [
        { value: 1, label: t('models.mainItems') },
        { value: 2, label: t('models.pizza') }
    ], [t]);

    const [modelo, setModelo] = useState(null);

    useEffect(() => {
        if (!showEditCategoria) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log("Modal Editar Categoria aberto com:", {
                _idCategoriaToEdit, id_categoria, _modeloToEdit, _titleToEdit, _disponibilidadeToEdit
            });
        }
    }, [showEditCategoria]);

    // Helper: clonar array de horarios com deep copy dos Sets
    const cloneHorarios = useCallback((horarios) => {
        return horarios.map(h => ({
            dias: new Set(h.dias),
            startTime: h.startTime,
            endTime: h.endTime
        }));
    }, []);

    const handleDiaHorarioDispChange = useCallback((currentHorarioIndex, key) => {
        setDiaHorarioDispToEdit(prev => {
            const newHorarios = cloneHorarios(prev);
            const horario = newHorarios[currentHorarioIndex];
            if (!horario) return prev;

            if (horario.dias.has(key)) {
                horario.dias.delete(key);
                // Remover string vazia se existir
                horario.dias.delete("");
            } else {
                horario.dias.add(key);
                // Remover string vazia se existir
                horario.dias.delete("");
            }
            return newHorarios;
        });
    }, [setDiaHorarioDispToEdit, cloneHorarios]);

    const SignUpSchema = Yup.object().shape({
        modeloFormik: Yup.mixed().required(t('actions.required')),
        title: Yup.string().required(t('actions.required')),
    });

    const formik = useFormik({
        validationSchema: SignUpSchema,
        validateOnBlur: false,
        validateOnChange: false,
        enableReinitialize: true,
        initialValues: {
            modeloFormik: modelo,
            title: _titleToEdit,
            disponibilidadeToEdit: _disponibilidadeToEdit
        },
        onSubmit: (values) => {
            const title = values.title;
            const disponibilidade = values.disponibilidadeToEdit;

            // Converter formato agrupado (Set de dias) para formato flat (1 objeto por dia)
            const horariosFlat = [];
            _diaHorarioDispToEdit.forEach(({ dias, startTime, endTime }) => {
                dias.forEach(dia => {
                    if (dia && dia !== "") {
                        horariosFlat.push({ dia, startTime, endTime });
                    }
                });
            });

            handleSubmitAPI(horariosFlat, title, disponibilidade);
        },
    });

    useEffect(() => {
        if (_modeloToEdit) {
            const modeloValue = typeof _modeloToEdit === 'object' && _modeloToEdit.value ? _modeloToEdit.value : _modeloToEdit;
            setModelo(modeloValue);
            formik.setFieldValue("modeloFormik", modeloValue);
        } else if (showEditCategoria) {
            setModelo(optionsModelo[0].value);
            formik.setFieldValue("modeloFormik", optionsModelo[0].value);
        }
    }, [showEditCategoria, _modeloToEdit]);

    const handleSubmitAPI = (horariosFlat, title, disponibilidade) => {
        const userID = user._id;
        const modeloSelecionado = optionsModelo.find(option => option.value === modelo);

        updateCategorias(userID, _idCategoriaToEdit, id_categoria, idEmpresa, modeloSelecionado, title, disponibilidade, horariosFlat).then(cast => {
            if (cast.status === 200) {
                toast(cast.data.msg, { autoClose: 5000, type: "success" });
                setRefresh(true);
                setEditCategoria(!showEditCategoria);
            } else {
                toast(cast.data.msg, { autoClose: 5000, type: "error" });
            }
        }).catch(err => {
            toast(err.response?.data?.msg || t('actions.deleteCategoryError'), { autoClose: 5000, type: "error" });
        });
    };

    const closeEditCategoria = () => setEditCategoria(!showEditCategoria);

    const handleDeleteHorario = useCallback((horarioIndex) => {
        setDiaHorarioDispToEdit(prev => {
            if (prev.length <= 1) {
                // Manter pelo menos 1 bloco - resetar ao invés de deletar
                return [{ dias: new Set(), startTime: "00:00", endTime: "23:59" }];
            }
            const next = [...prev];
            next.splice(horarioIndex, 1);
            return next;
        });
    }, [setDiaHorarioDispToEdit]);

    const handleOptionChange = (event) => {
        const newValue = event.target.value;
        if (newValue === "sempre" || newValue === "pausado") {
            setDiaHorarioDispToEdit([{ dias: new Set(), startTime: "00:00", endTime: "23:59" }]);
        }
        formik.setFieldValue("disponibilidadeToEdit", newValue);
    };

    const handleTimeChange = useCallback((horarioIndex, field, event) => {
        const value = event.target.value;
        setDiaHorarioDispToEdit(prev => {
            const newHorarios = cloneHorarios(prev);
            if (newHorarios[horarioIndex]) {
                newHorarios[horarioIndex][field] = value;
            }
            return newHorarios;
        });
    }, [setDiaHorarioDispToEdit, cloneHorarios]);

    const handleAddHorario = useCallback(() => {
        setDiaHorarioDispToEdit(prev => [
            ...prev,
            { dias: new Set(), startTime: "00:00", endTime: "23:59" }
        ]);
    }, [setDiaHorarioDispToEdit]);

    return (
        <div className={showEditCategoria ? 'pdj-modal-overlay' : 'pdj-modal-overlay--hidden'}>
            <div className="pdj-modal-box pdj-modal-box--narrow">
                <form onSubmit={formik.handleSubmit}>

                    {/* Header */}
                    <div className="pdj-modal-header">
                        <div className="pdj-modal-header-text">
                            <h2 className="pdj-modal-title">{t('editCategoryModal.title')}</h2>
                            <p className="pdj-modal-subtitle">{t('editCategoryModal.subtitle')}</p>
                        </div>
                        <button type="button" className="pdj-modal-close-btn" onClick={closeEditCategoria}>
                            <IoClose />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="pdj-modal-body">

                        {/* Modelo + Nome */}
                        <div className="pdj-modal-grid-2">
                            <div className="pdj-modal-field">
                                <label className="pdj-modal-label">
                                    {t('editCategoryModal.groupType')}
                                    <span className="pdj-modal-required">*</span>
                                </label>
                                <select
                                    className="pdj-modal-select"
                                    id="modeloFormik"
                                    name="modeloFormik"
                                    value={modelo || ''}
                                    onChange={(e) => {
                                        setModelo(Number(e.target.value));
                                        formik.setFieldValue("modeloFormik", Number(e.target.value));
                                    }}
                                >
                                    {optionsModelo.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                {formik.errors.modeloFormik && (
                                    <span className="pdj-modal-error">{formik.errors.modeloFormik}</span>
                                )}
                            </div>

                            <div className="pdj-modal-field">
                                <label className="pdj-modal-label">
                                    {t('editCategoryModal.categoryName')}
                                    <span className="pdj-modal-required">*</span>
                                </label>
                                <input
                                    className="pdj-modal-input"
                                    placeholder={t('editCategoryModal.categoryNamePlaceholder')}
                                    id="title"
                                    type="text"
                                    name="title"
                                    onChange={formik.handleChange}
                                    value={formik.values.title}
                                />
                                {formik.errors.title && (
                                    <span className="pdj-modal-error">{formik.errors.title}</span>
                                )}
                            </div>
                        </div>

                        <hr className="pdj-modal-divider" />

                        {/* Disponibilidade */}
                        <div className="pdj-modal-field">
                            <label className="pdj-modal-label">
                                {t('availability.label')}
                                <span className="pdj-modal-required">*</span>
                            </label>
                            <select
                                className="pdj-modal-select"
                                id="disponibilidadeToEdit"
                                name="disponibilidadeToEdit"
                                value={formik.values.disponibilidadeToEdit}
                                onChange={handleOptionChange}
                            >
                                {options.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Horarios especificos */}
                        {formik.values.disponibilidadeToEdit === "especificos" && (
                            <>
                                {_diaHorarioDispToEdit.map((horario, horarioIndex) => (
                                    <div key={horarioIndex} className="pdj-modal-schedule-block">

                                        {/* Header do bloco */}
                                        <div className="pdj-modal-schedule-block-header">
                                            <span className="pdj-modal-schedule-block-number">
                                                {t('schedule.scheduleNumber', { number: horarioIndex + 1 })}
                                            </span>
                                            <button
                                                type="button"
                                                className="pdj-modal-btn pdj-modal-btn--danger"
                                                onClick={() => handleDeleteHorario(horarioIndex)}
                                                style={{ padding: '0 8px', height: '28px', fontSize: '11px' }}
                                            >
                                                <FaTrash size={10} />
                                            </button>
                                        </div>

                                        {/* Dias da semana */}
                                        <div className="pdj-modal-field" style={{ gap: '4px' }}>
                                            <label className="pdj-modal-label" style={{ fontSize: '12px', color: '#64748b' }}>
                                                {t('schedule.availableDays')}
                                            </label>
                                            <div className="pdj-modal-day-chips">
                                                {DAY_KEYS.map((key) => (
                                                    <button
                                                        key={key}
                                                        type="button"
                                                        className={`pdj-modal-day-chip${horario.dias.has(key) ? ' pdj-modal-day-chip--active' : ''}`}
                                                        onClick={() => handleDiaHorarioDispChange(horarioIndex, key)}
                                                    >
                                                        {t(`schedule.days.${key}`)}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Horarios */}
                                        <div className="pdj-modal-schedule-item">
                                            <input
                                                type="time"
                                                className="pdj-modal-input"
                                                value={horario.startTime}
                                                onChange={(event) => handleTimeChange(horarioIndex, "startTime", event)}
                                            />
                                            <span className="pdj-modal-schedule-separator">{t('schedule.to')}</span>
                                            <input
                                                type="time"
                                                className="pdj-modal-input"
                                                value={horario.endTime}
                                                onChange={(event) => handleTimeChange(horarioIndex, "endTime", event)}
                                            />
                                        </div>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    className="pdj-modal-btn pdj-modal-btn--secondary"
                                    onClick={handleAddHorario}
                                    style={{ alignSelf: 'flex-start' }}
                                >
                                    <BsPlusCircle />
                                    {t('schedule.addTime')}
                                </button>
                            </>
                        )}

                    </div>

                    {/* Footer */}
                    <div className="pdj-modal-footer">
                        <button type="button" className="pdj-modal-btn pdj-modal-btn--secondary" onClick={closeEditCategoria}>
                            {t('actions.cancel')}
                        </button>
                        <button type="submit" className="pdj-modal-btn pdj-modal-btn--primary">
                            {t('actions.save')}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default ModalEditCategoria;
